import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { guestsApi } from '../../lib/api';
import { Plus, Search, User, Mail, Phone, History, Edit, Globe } from 'lucide-react';

export default function GuestsPage() {
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [profileType, setProfileType] = useState('all');
  const [vipOnly, setVipOnly] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState(null);
  const [historyGuest, setHistoryGuest] = useState(null);
  const [guestHistory, setGuestHistory] = useState([]);

  const fetchGuests = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (profileType !== 'all') params.profile_type = profileType;
      if (vipOnly) params.vip_only = true;
      const response = await guestsApi.list(params);
      setGuests(response.data);
    } catch (error) {
      toast.error('Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, [search, profileType, vipOnly]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const handleCreateGuest = async (formData) => {
    try {
      await guestsApi.create(formData);
      toast.success('Guest created successfully');
      setIsDialogOpen(false);
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create guest');
    }
  };

  const handleUpdateGuest = async (formData) => {
    try {
      await guestsApi.update(editingGuest.id, formData);
      toast.success('Guest updated successfully');
      setEditingGuest(null);
      fetchGuests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update guest');
    }
  };

  const loadGuestHistory = async (guest) => {
    try {
      const response = await guestsApi.getHistory(guest.id);
      setGuestHistory(response.data);
      setHistoryGuest(guest);
    } catch (error) {
      toast.error('Failed to load guest history');
    }
  };

  return (
    <div className="space-y-6" data-testid="guests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Guest Profiles</h1>
          <p className="text-muted-foreground">Manage guest information and history</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-guest-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Guest
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Guest</DialogTitle>
            </DialogHeader>
            <GuestForm onSubmit={handleCreateGuest} onCancel={() => setIsDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, phone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="guest-search-input"
                />
              </div>
            </div>
            <Select value={profileType} onValueChange={setProfileType}>
              <SelectTrigger className="w-[180px]" data-testid="profile-type-select">
                <SelectValue placeholder="Profile Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                <SelectItem value="COMPANY">Company</SelectItem>
                <SelectItem value="TRAVEL_AGENT">Travel Agent</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={vipOnly ? 'default' : 'outline'}
              onClick={() => setVipOnly(!vipOnly)}
              data-testid="vip-filter-btn"
            >
              VIP Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Guests Table */}
      <Card>
        <CardContent className="p-0">
          <Table data-testid="guests-table">
            <TableHeader>
              <TableRow>
                <TableHead>Guest</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>VIP</TableHead>
                <TableHead>Stays</TableHead>
                <TableHead>Revenue</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : guests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground" data-testid="empty-guests">
                    No guests found. Add your first guest to get started.
                  </TableCell>
                </TableRow>
              ) : (
                guests.map((guest) => (
                  <TableRow key={guest.id} data-testid={`guest-row-${guest.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{guest.title} {guest.first_name} {guest.last_name}</div>
                          {guest.company_name && <div className="text-sm text-muted-foreground">{guest.company_name}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {guest.email && (
                          <div className="flex items-center gap-1 text-sm">
                            <Mail className="h-3 w-3" /> {guest.email}
                          </div>
                        )}
                        {guest.phone && (
                          <div className="flex items-center gap-1 text-sm">
                            <Phone className="h-3 w-3" /> {guest.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{guest.profile_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {guest.vip_code && <Badge className="bg-amber-500 text-white">{guest.vip_code}</Badge>}
                    </TableCell>
                    <TableCell>{guest.total_stays || 0}</TableCell>
                    <TableCell>{guest.total_revenue ? `ETB ${guest.total_revenue.toLocaleString()}` : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => loadGuestHistory(guest)} data-testid={`guest-history-${guest.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEditingGuest(guest)} data-testid={`edit-guest-${guest.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingGuest} onOpenChange={() => setEditingGuest(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Guest</DialogTitle>
          </DialogHeader>
          {editingGuest && (
            <GuestForm
              guest={editingGuest}
              onSubmit={handleUpdateGuest}
              onCancel={() => setEditingGuest(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={!!historyGuest} onOpenChange={() => setHistoryGuest(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Stay History - {historyGuest?.first_name} {historyGuest?.last_name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-auto">
            {guestHistory.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No stay history found.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Confirmation</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {guestHistory.map((stay) => (
                    <TableRow key={stay.id}>
                      <TableCell className="font-mono">{stay.confirmation_number}</TableCell>
                      <TableCell>{stay.properties?.name}</TableCell>
                      <TableCell>{stay.arrival_date} to {stay.departure_date}</TableCell>
                      <TableCell><Badge variant="secondary">{stay.status}</Badge></TableCell>
                      <TableCell>ETB {stay.room_total?.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GuestForm({ guest, onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    profile_type: guest?.profile_type || 'INDIVIDUAL',
    title: guest?.title || '',
    first_name: guest?.first_name || '',
    last_name: guest?.last_name || '',
    email: guest?.email || '',
    phone: guest?.phone || '',
    mobile: guest?.mobile || '',
    nationality: guest?.nationality || '',
    id_type: guest?.id_type || '',
    id_number: guest?.id_number || '',
    company_name: guest?.company_name || '',
    address_city: guest?.address_city || '',
    address_country: guest?.address_country || '',
    vip_code: guest?.vip_code || '',
    notes: guest?.notes || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="guest-form">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Profile Type</Label>
          <Select value={formData.profile_type} onValueChange={(v) => setFormData({ ...formData, profile_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="INDIVIDUAL">Individual</SelectItem>
              <SelectItem value="COMPANY">Company</SelectItem>
              <SelectItem value="TRAVEL_AGENT">Travel Agent</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Title</Label>
          <Select value={formData.title} onValueChange={(v) => setFormData({ ...formData, title: v })}>
            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Mr.">Mr.</SelectItem>
              <SelectItem value="Mrs.">Mrs.</SelectItem>
              <SelectItem value="Ms.">Ms.</SelectItem>
              <SelectItem value="Dr.">Dr.</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>First Name *</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            required
            data-testid="guest-firstname-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Last Name *</Label>
          <Input
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            required
            data-testid="guest-lastname-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            data-testid="guest-email-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            data-testid="guest-phone-input"
          />
        </div>
      </div>

      {formData.profile_type === 'COMPANY' && (
        <div className="space-y-2">
          <Label>Company Name</Label>
          <Input
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nationality</Label>
          <Input
            value={formData.nationality}
            onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
            placeholder="e.g., Ethiopian"
          />
        </div>
        <div className="space-y-2">
          <Label>VIP Code</Label>
          <Select value={formData.vip_code || 'none'} onValueChange={(v) => setFormData({ ...formData, vip_code: v === 'none' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="VIP">VIP</SelectItem>
              <SelectItem value="VVIP">VVIP</SelectItem>
              <SelectItem value="PLATINUM">Platinum</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>ID Type</Label>
          <Select value={formData.id_type} onValueChange={(v) => setFormData({ ...formData, id_type: v })}>
            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="PASSPORT">Passport</SelectItem>
              <SelectItem value="NATIONAL_ID">National ID</SelectItem>
              <SelectItem value="DRIVERS_LICENSE">Driver&apos;s License</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>ID Number</Label>
          <Input
            value={formData.id_number}
            onChange={(e) => setFormData({ ...formData, id_number: e.target.value })}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" data-testid="guest-submit-btn">{guest ? 'Update' : 'Create'} Guest</Button>
      </div>
    </form>
  );
}
